import * as React from "react";
import { Spacer, AppCSS, ChkInputBox } from "../../../../components";
import { Container } from "../../../../globalstyled";
import { ColDiv, RowDiv } from "../../../../components/misc/misc.styled";
import { Box, Typography, Chip, Checkbox } from "@mui/material";
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface MaterialTableProps {
  materialMeasurements: any[];
  onOrderStatusChange?: (
    productSystemId: number,
    statusType: 'orderSent' | 'orderConfirmed',
    value: boolean,
    currentOrderSent: boolean,
    currentOrderConfirmed: boolean,
    productSystemName?: string
  ) => void;
  onChangeOrderSent?: (
    productSystemId: number,
    productSystemName?: string,
    existingSupplierId?: number,
    existingJobStartDate?: string
  ) => void;
}

const truncateText = (text: string, length: number) => {
  if (!text) return ""; // Return empty string if no text
  return text.length > length ? text.substring(0, length) + "..." : text;
};

export const MaterialTable: React.FC<MaterialTableProps> = ({
  materialMeasurements,
  onOrderStatusChange,
  onChangeOrderSent,
}) => {
  // Handle order status change
  const handleOrderStatusChange = (
    productSystemId: number,
    statusType: 'orderSent' | 'orderConfirmed',
    value: boolean,
    currentOrderSent: boolean,
    currentOrderConfirmed: boolean,
    productSystemName?: string
  ) => {
    // Prevent checkbox from updating by returning false
    // The parent will handle confirmation and update the data source
    if (onOrderStatusChange) {
      onOrderStatusChange(productSystemId, statusType, value, currentOrderSent, currentOrderConfirmed, productSystemName);
    }
  };
  const Table = () => {
    return (
      <Box sx={{ width: "100%" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {materialMeasurements?.map((measurementCategory, index) => {
            const selectedMaterials = measurementCategory?.measurements
              .flatMap(
                (measurement: any) => measurement?.associatedMaterials || []
              )
              .filter((material: any) => material?.isSelected);

            if (selectedMaterials.length === 0) {
              return null;
            }

            return (
              <Box key={index}>
                {/* Product System Header */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: { xs: "flex-start", md: "center" },
                    justifyContent: "space-between",
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: 18, md: 20 },
                      fontWeight: 700,
                      color: "#1f2937",
                      borderBottom: "3px solid #4b87ed",
                      pb: 0.5,
                    }}
                  >
                    {measurementCategory?.name}
                  </Typography>

                  {/* Order Status Controls */}
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                    <Box
                      onClick={(e) => {
                        e.preventDefault();
                        handleOrderStatusChange(
                          measurementCategory.productId,
                          "orderSent",
                          !measurementCategory.orderSent,
                          measurementCategory.orderSent || false,
                          measurementCategory.orderConfirmed || false,
                          measurementCategory.name
                        );
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 2,
                        py: 1,
                        borderRadius: "8px",
                        backgroundColor: measurementCategory.orderSent
                          ? "#dcfce7"
                          : "#f3f4f6",
                        border: `2px solid ${
                          measurementCategory.orderSent ? "#22c55e" : "#e5e7eb"
                        }`,
                        cursor: "pointer",
                        transition: "all 0.2s",
                        "&:hover": {
                          backgroundColor: measurementCategory.orderSent
                            ? "#bbf7d0"
                            : "#e5e7eb",
                        },
                      }}
                    >
                      <Checkbox
                        checked={measurementCategory.orderSent || false}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          padding: 0,
                          color: "#9ca3af",
                          "&.Mui-checked": {
                            color: "#22c55e",
                          },
                        }}
                        size="small"
                      />
                      <LocalShippingIcon
                        sx={{
                          fontSize: 18,
                          color: measurementCategory.orderSent
                            ? "#22c55e"
                            : "#6b7280",
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: measurementCategory.orderSent
                            ? "#166534"
                            : "#374151",
                        }}
                      >
                        Order Sent
                      </Typography>
                    </Box>

                    <Box
                      onClick={(e) => {
                        e.preventDefault();
                        if (measurementCategory.orderSent) {
                          handleOrderStatusChange(
                            measurementCategory.productId,
                            "orderConfirmed",
                            !measurementCategory.orderConfirmed,
                            measurementCategory.orderSent || false,
                            measurementCategory.orderConfirmed || false,
                            measurementCategory.name
                          );
                        }
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 2,
                        py: 1,
                        borderRadius: "8px",
                        backgroundColor: measurementCategory.orderConfirmed
                          ? "#dcfce7"
                          : "#f3f4f6",
                        border: `2px solid ${
                          measurementCategory.orderConfirmed
                            ? "#22c55e"
                            : "#e5e7eb"
                        }`,
                        cursor: measurementCategory.orderSent
                          ? "pointer"
                          : "not-allowed",
                        opacity: measurementCategory.orderSent ? 1 : 0.5,
                        transition: "all 0.2s",
                        "&:hover": measurementCategory.orderSent
                          ? {
                              backgroundColor: measurementCategory.orderConfirmed
                                ? "#bbf7d0"
                                : "#e5e7eb",
                            }
                          : {},
                      }}
                    >
                      <Checkbox
                        checked={measurementCategory.orderConfirmed || false}
                        disabled={!measurementCategory.orderSent}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          padding: 0,
                          color: "#9ca3af",
                          "&.Mui-checked": {
                            color: "#22c55e",
                          },
                          "&.Mui-disabled": {
                            color: "#d1d5db",
                          },
                        }}
                        size="small"
                      />
                      <CheckCircleIcon
                        sx={{
                          fontSize: 18,
                          color: measurementCategory.orderConfirmed
                            ? "#22c55e"
                            : "#6b7280",
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: measurementCategory.orderConfirmed
                            ? "#166534"
                            : "#374151",
                        }}
                      >
                        Order Confirmed
                      </Typography>
                    </Box>

                    {/* Change Order Sent Button - only show when order is sent */}
                    {measurementCategory.orderSent && onChangeOrderSent && (
                      <Box
                        onClick={(e) => {
                          e.preventDefault();
                          onChangeOrderSent(
                            measurementCategory.productId,
                            measurementCategory.name,
                            measurementCategory.supplierId,
                            measurementCategory.jobStartDate
                          );
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          px: 2,
                          py: 1,
                          borderRadius: "8px",
                          backgroundColor: "#f3f4f6",
                          border: "2px solid #3b82f6",
                          cursor: "pointer",
                          transition: "all 0.2s",
                          "&:hover": {
                            backgroundColor: "#dbeafe",
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#3b82f6",
                          }}
                        >
                          Change Order Sent
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Desktop Table View */}
                <Box
                  sx={{
                    display: { xs: "none", md: "block" },
                    overflowX: "auto",
                    border: "1px solid #e5e7eb",
                    borderRadius: "12px",
                    mb: 3,
                  }}
                >
                  <Box
                    component="table"
                    sx={{
                      width: "100%",
                      borderCollapse: "collapse",
                      minWidth: "800px",
                    }}
                  >
                    <Box component="thead" sx={{ backgroundColor: "#f8f9fa" }}>
                      <Box component="tr">
                        <Box
                          component="th"
                          sx={{
                            py: 2,
                            px: 2,
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#6b7280",
                            textAlign: "left",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Brand
                        </Box>
                        <Box
                          component="th"
                          sx={{
                            py: 2,
                            px: 2,
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#6b7280",
                            textAlign: "left",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Description
                        </Box>
                        <Box
                          component="th"
                          sx={{
                            py: 2,
                            px: 2,
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#6b7280",
                            textAlign: "left",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Quality
                        </Box>
                        <Box
                          component="th"
                          sx={{
                            py: 2,
                            px: 2,
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#6b7280",
                            textAlign: "left",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Coverage (per unit)
                        </Box>
                        <Box
                          component="th"
                          sx={{
                            py: 2,
                            px: 2,
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#6b7280",
                            textAlign: "left",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Coverage Area
                        </Box>
                        <Box
                          component="th"
                          sx={{
                            py: 2,
                            px: 2,
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#6b7280",
                            textAlign: "left",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Units Needed
                        </Box>
                        <Box
                          component="th"
                          sx={{
                            py: 2,
                            px: 2,
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#6b7280",
                            textAlign: "left",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Total Price
                        </Box>
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {selectedMaterials.map((material, idx) => (
                        <Box
                          component="tr"
                          key={idx}
                          sx={{
                            borderTop: "1px solid #e5e7eb",
                            "&:hover": {
                              backgroundColor: "#f9fafb",
                            },
                          }}
                        >
                          <Box
                            component="td"
                            sx={{
                              py: 2,
                              px: 2,
                              fontSize: 14,
                              color: "#1f2937",
                            }}
                          >
                            {material?.brand || "N/A"}
                          </Box>
                          <Box
                            component="td"
                            sx={{
                              py: 2,
                              px: 2,
                              fontSize: 14,
                              color: "#6b7280",
                            }}
                          >
                            {material?.description || "N/A"}
                          </Box>
                          <Box
                            component="td"
                            sx={{
                              py: 2,
                              px: 2,
                              fontSize: 14,
                              color: "#1f2937",
                            }}
                          >
                            {material?.quality}
                          </Box>
                          <Box
                            component="td"
                            sx={{
                              py: 2,
                              px: 2,
                              fontSize: 14,
                              color: "#1f2937",
                            }}
                          >
                            {material?.coverageAmount} {material?.measurementUnit}
                          </Box>
                          <Box
                            component="td"
                            sx={{
                              py: 2,
                              px: 2,
                              fontSize: 14,
                              color: "#1f2937",
                            }}
                          >
                            {measurementCategory?.measurements?.find(
                              (measurement: any) =>
                                measurement.id === material.measurementId
                            )?.value?.quantity || "N/A"}{" "}
                            {material?.measurementUnit}
                          </Box>
                          <Box
                            component="td"
                            sx={{
                              py: 2,
                              px: 2,
                              fontSize: 14,
                              color: "#1f2937",
                            }}
                          >
                            {measurementCategory?.measurements?.find(
                              (measurement: any) =>
                                measurement.id === material.measurementId
                            )?.value?.unitsNeeded || "N/A"}
                          </Box>
                          <Box
                            component="td"
                            sx={{
                              py: 2,
                              px: 2,
                              fontSize: 14,
                              fontWeight: 600,
                              color: "#1f2937",
                            }}
                          >
                            ${material?.price}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>

                {/* Mobile Card View */}
                <Box sx={{ display: { xs: "flex", md: "none" }, flexDirection: "column", gap: 2 }}>
                  {selectedMaterials.map((material, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                        borderRadius: "12px",
                        p: 2,
                        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                      }}
                    >
                      <Box sx={{ display: "grid", gap: 1.5 }}>
                        <Box>
                          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#6b7280", mb: 0.5 }}>
                            Brand
                          </Typography>
                          <Typography sx={{ fontSize: 14, color: "#1f2937" }}>
                            {material?.brand || "N/A"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#6b7280", mb: 0.5 }}>
                            Description
                          </Typography>
                          <Typography sx={{ fontSize: 14, color: "#6b7280" }}>
                            {material?.description || "N/A"}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                          <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#6b7280", mb: 0.5 }}>
                              Quality
                            </Typography>
                            <Typography sx={{ fontSize: 14, color: "#1f2937" }}>
                              {material?.quality}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#6b7280", mb: 0.5 }}>
                              Price
                            </Typography>
                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#1f2937" }}>
                              ${material?.price}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                          <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#6b7280", mb: 0.5 }}>
                              Coverage/Unit
                            </Typography>
                            <Typography sx={{ fontSize: 14, color: "#1f2937" }}>
                              {material?.coverageAmount} {material?.measurementUnit}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#6b7280", mb: 0.5 }}>
                              Units Needed
                            </Typography>
                            <Typography sx={{ fontSize: 14, color: "#1f2937" }}>
                              {measurementCategory?.measurements?.find(
                                (measurement: any) =>
                                  measurement.id === material.measurementId
                              )?.value?.unitsNeeded || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  return Table();
};
